import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { postDuplicateDocument } from '~extension/web2print/controller/api/duplicateForm';
import { FieldValue } from '~extension/web2print/model/FieldSettings';
import { makeFormResponse } from '~extension/web2print/model/FormResponseFactory';
import { FormSettings } from '~extension/web2print/model/FormSettings';
import { FieldGroup } from '~extension/web2print/view/DuplicateForm/FieldGroup';
import { AssetId } from '~modules/media-library/model/Asset';
import { makeProgramaticallyLinkClick } from '~utils/link';

type DuplicateFormProps = {
  formSettings: FormSettings;
  onClose?: () => void;
};

export function DuplicateForm({ formSettings, onClose }: DuplicateFormProps) {
  const [formData, setFormData] = useState<Record<string, FieldValue>>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const useDuplicateForm = (assetId: AssetId) => {
    return useMutation({
      mutationFn: async (postData: object) => makeFormResponse(await postDuplicateDocument(assetId, postData)),
    });
  };
  const { mutate, data } = useDuplicateForm(formSettings.assetId);

  useEffect(() => {
    if (data !== undefined && data.status === 'success' && isComplete === false) {
      const href = '/tools/web2print/editor/' + data.clientId + '/' + data.jobId;
      makeProgramaticallyLinkClick({ href });
      setIsComplete(true);
    }
    setIsLoading(false);
  }, [data, isComplete, onClose]);

  useEffect(() => {
    Object.keys(formSettings.fields).forEach(key => {
      setFormData(currentData => {
        return { ...currentData, [formSettings.fields[key].fieldname]: formSettings.fields[key].value };
      });
    });
  }, [formSettings]);

  // Handler: save changes
  const onSubmit = useCallback(() => {
    setIsLoading(true);
    const postData = new FormData();
    Object.keys(formData).forEach(key => {
      if (Array.isArray(formData[key])) {
        Object.values(formData[key]).forEach(item => {
          postData.append(String(key) + '[]', String(item));
        });
      } else {
        postData.append(key, String(formData[key]));
      }
    });

    mutate(postData);
  }, [formData, mutate]);

  const onValueChanged = (key: string, value: FieldValue) => {
    setFormData(currentData => {
      return { ...currentData, [key]: value };
    });
  };

  if (isLoading) {
    return <div className="loading"></div>;
  }

  if (isComplete) {
    return (
      <div>
        <h3 className="mx-3 mb-4 py-2">We have received your data.</h3>
        <p className="pl-3 py-2 pr-8">
          The template has been created. It will open in a separate browser tab if you click on it.
        </p>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <h3 className="mx-3 mb-4 py-2">Metadata for this document</h3>
      <div className="web2print-duplicate-form container py-1 px-5">
        <form>
          {formSettings.fields.map(field => {
            const fieldError: string =
              data !== undefined && data.form_errors && data.form_errors[field.key] !== undefined
                ? String(data.form_errors[field.key])
                : '';
            const fieldValue = formData[field.fieldname] !== undefined ? formData[field.fieldname] : field.value;
            return (
              <FieldGroup
                key={field.key}
                fieldSettings={field}
                fieldValue={fieldValue}
                error={fieldError}
                onValueChanged={onValueChanged}
              />
            );
          })}
        </form>
        <div className="flex my-4 justify-center">
          <button className="button c-button c-button--dark u-padding-horizontal-lg mx-2" onClick={onSubmit}>
            OK
          </button>
          <button className="button c-button c-button--light u-padding-horizontal-lg mx-2" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
